import { useForm } from 'react-hook-form';

import { Dashboard } from 'types/dashboard';

import { useModal } from 'hooks/useModal';
import { useUpdateDashboard } from 'dashboards/hooks/useUpdateDashboard';

import * as Dialog from 'components/Dialog';
import { Button } from 'components/Button/Button';
import { Field } from 'components/Field/Field';
import { FormErrorMessage } from 'components/FormMessage/FormMessage';
import { Heading } from 'components/Heading/Heading';
import { Input } from 'components/Input/Input';

export type RenameDashboardDialogProps = Pick<Dialog.RootProps, 'open'> & {
  dashboard: Dashboard;
};

type RenameDashboardFieldValues = {
  name: string;
};

export function RenameDashboardDialog({
  dashboard,
  open,
}: RenameDashboardDialogProps) {
  const { close } = useModal();

  return (
    <Dialog.Root
      className="min-w-md"
      open={open}
      onOpenChange={(open) => !open && close()}
    >
      <Dialog.Title asChild>
        <Heading level="2">Rename dashboard</Heading>
      </Dialog.Title>

      <RenameDashboardContent dashboard={dashboard} />
    </Dialog.Root>
  );
}

function RenameDashboardContent({ dashboard }: RenameDashboardDialogProps) {
  const { close } = useModal();

  const {
    formState: { errors },
    handleSubmit,
    register,
  } = useForm<RenameDashboardFieldValues>({
    defaultValues: {
      name: dashboard.name,
    },
  });

  const {
    isLoading: isRenaming,
    error,
    mutate: updateDashboard,
  } = useUpdateDashboard({
    onSuccess: () => close(),
  });

  function onSubmit({ name }: RenameDashboardFieldValues) {
    updateDashboard({ id: dashboard.id, name });
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <Field
        id="dashboard-name"
        label="Dashboard name"
        error={errors.name}
        required
      >
        <Input
          {...register('name', {
            required: 'Please enter a name for this dashboard.',
          })}
          id="dashboard-name"
          type="text"
          autoComplete="off"
          spellCheck="false"
          placeholder={dashboard.name}
          autoFocus
        />
      </Field>

      <FormErrorMessage error={error} />

      <div className="flex justify-end gap-2">
        <Dialog.Close asChild>
          <Button variant="secondary">Cancel</Button>
        </Dialog.Close>
        <Button variant="primary" type="submit" loading={isRenaming}>
          Save
        </Button>
      </div>
    </form>
  );
}
